import { createContext, useContext, useState, useEffect } from 'react';

/**
 * HelpIconContext - Global state for contextual help icons visibility
 * 
 * Usage:
 * const { helpIconsEnabled, toggleHelpIcons } = useHelpIcons();
 */

const HelpIconContext = createContext();

export function HelpIconProvider({ children }) {
  const [helpIconsEnabled, setHelpIconsEnabled] = useState(() => {
    // Default to FALSE for new users (off by default), persist preference
    const saved = localStorage.getItem('keymade_help_icons_enabled');
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('keymade_help_icons_enabled', JSON.stringify(helpIconsEnabled));
  }, [helpIconsEnabled]);

  const toggleHelpIcons = () => {
    setHelpIconsEnabled(prev => !prev);
  };

  const enableHelpIcons = () => setHelpIconsEnabled(true);
  const disableHelpIcons = () => setHelpIconsEnabled(false);

  return (
    <HelpIconContext.Provider value={{ 
      helpIconsEnabled, 
      toggleHelpIcons,
      enableHelpIcons,
      disableHelpIcons
    }}>
      {children}
    </HelpIconContext.Provider>
  );
}

export function useHelpIcons() {
  const context = useContext(HelpIconContext);
  if (!context) {
    // Return default values if used outside provider (default OFF)
    return { 
      helpIconsEnabled: false, 
      toggleHelpIcons: () => {},
      enableHelpIcons: () => {},
      disableHelpIcons: () => {}
    };
  }
  return context;
}

export default HelpIconContext;
